import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  ANNOUNCEMENT_ROLES,
  CONTRACTOR_ANNOUNCEMENT_ROLE,
  INTERNAL_ANNOUNCEMENT_ROLES,
} from "../types";
import type { Announcement, AnnouncementFormData } from "../types";

interface Props {
  announcement?: Announcement | null;
  onSave: (data: AnnouncementFormData) => Promise<void>;
  onClose: () => void;
}

export function AnnouncementFormModal({ announcement, onSave, onClose }: Props) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<AnnouncementFormData>({
    title: announcement?.title ?? "",
    body: announcement?.body ?? "",
    // Subcontractors are never part of the default audience — picked on purpose only.
    allowed_roles:
      announcement?.allowed_roles ?? INTERNAL_ANNOUNCEMENT_ROLES.map((r) => r.value),
    is_important: announcement?.is_important ?? false,
  });

  const toggleRole = (value: string) =>
    setForm((f) => ({
      ...f,
      allowed_roles: f.allowed_roles.includes(value)
        ? f.allowed_roles.filter((r) => r !== value)
        : [...f.allowed_roles, value],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={announcement ? "Edytuj komunikat" : "Nowy komunikat"} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Tytuł *
          </label>
          <input
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Treść
          </label>
          <textarea
            rows={8}
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-y"
          />
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Odbiorcy (poziom dostępu)
          </p>
          <div className="flex flex-wrap gap-3">
            {ANNOUNCEMENT_ROLES.map((r) => (
              <label key={r.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.allowed_roles.includes(r.value)}
                  onChange={() => toggleRole(r.value)}
                  className="w-4 h-4"
                />
                {r.label}
              </label>
            ))}
          </div>
          {form.allowed_roles.includes(CONTRACTOR_ANNOUNCEMENT_ROLE) ? (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              Komunikat zobaczą podwykonawcy (firmy zewnętrzne) — nie umieszczaj w nim
              informacji wewnętrznych.
            </p>
          ) : (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Podwykonawcy widzą tylko komunikaty z zaznaczoną rolą „Podwykonawca”.
            </p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.is_important}
              onChange={(e) => setForm((f) => ({ ...f, is_important: e.target.checked }))}
              className="w-4 h-4"
            />
            Ważny komunikat
          </label>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Trafia na samą górę listy i pulsuje u odbiorców, dopóki go nie przeczytają.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
          >
            {saving ? "Zapisywanie..." : "Zapisz jako proponowany"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
